// The DM's call for initiative as a player reads it (qa-runs 405, Tom's decision of 2026-10-29): the DM's Roll monsters
// throws for every combatant no player owns, and each player is asked for their own — *Roll initiative for Shelby* —
// with Roll (the system's initiative dice plus their bonus, thrown by the server and shown in the roll log) and a box for
// what they rolled on physical dice (the typed roll of issue 1050). Several combatants of theirs, a line for each.
// Drawn only from this player's own projection (`prompt`): another player's prompt, or who else is asked, never
// reaches them. A line goes as its combatant has a roll, however it got one.
import { useState } from 'react';
import type { AdvantageWord, PlayerEncounter } from '@dragons-table/vtt-shared';
import { AdvantageControl } from './AdvantageControl';
import { useTableState } from '../../table/TableState';
import { useViewOnlyFor } from '../../table/mayAct';
import { VIEW_ONLY_REASON } from '../../table/viewOnly';
import { BTN, PRIMARY_SM } from '../ui';

function PromptLine({ id, name }: { id: string; name: string }) {
  const { act, setNotice, systemRules } = useTableState();
  const locked = useViewOnlyFor()('encounter.set');
  const [typed, setTyped] = useState('');
  const [word, setWord] = useState<AdvantageWord | null>(null);
  const said = (r: { ok: boolean; reason?: string }) => { if (!r.ok) setNotice(r.reason ?? 'Your initiative could not be set.'); };
  const send = () => {
    const n = Math.trunc(Number(typed));
    if (typed.trim() === '' || !Number.isFinite(n)) return;
    act({ kind: 'encounter.set', id, initRoll: n }, said);
  };
  return (
    <li className="flex flex-wrap items-center gap-1.5" data-testid={`initiative-prompt-${id}`}>
      <span className="font-semibold text-amber-200">Roll initiative for {name}</span>
      {/* No initiative rules, nothing the server throws (issue 1305): the roll is typed. */}
      {systemRules.initiative && (
        <button type="button" className={PRIMARY_SM} aria-label={`Roll initiative for ${name}`} disabled={locked} title={locked ? VIEW_ONLY_REASON : 'The game system\'s initiative dice plus your bonus, shown in the roll log'}
          onClick={() => act({ kind: 'encounter.roll', ids: [id], ...(word ? { advantage: word } : {}) }, said)}>Roll</button>
      )}
      {systemRules.initiative && systemRules.initiativeAdvantage && <AdvantageControl id={`initiative-${id}`} label={`Advantage on ${name}'s initiative`} value={word} onChange={(a) => setWord(a === 'normal' ? null : a)} />}
      <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input aria-label={`What you rolled for ${name}`} type="number" inputMode="numeric" className="w-14 rounded bg-stone-800 px-1 py-1 text-sm" placeholder="or type"
          title="What you rolled on your own dice, your bonus added" disabled={locked} value={typed} onChange={(e) => setTyped(e.target.value)} />
        <button type="submit" className={BTN} disabled={locked || typed.trim() === ''} aria-label={`Set initiative for ${name}`}>Set</button>
      </form>
    </li>
  );
}

/** A player's prompt, a line per combatant of theirs the DM's call still waits for; nothing for the DM or without one. */
export function InitiativePrompt() {
  const { store, role } = useTableState();
  if (role === 'dm') return null;
  const lines = (store.state.encounter as PlayerEncounter | undefined)?.prompt ?? [];
  if (!lines.length) return null;
  return (
    <ul role="group" aria-label="The DM calls for initiative" className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded border border-amber-700 bg-amber-950/40 px-2 py-1 text-sm" data-testid="initiative-prompt">
      {lines.map((l) => <PromptLine key={l.id} id={l.id} name={l.name} />)}
    </ul>
  );
}
